<template>
  <div>
    <div class="header-contest">
      <img
        :src="page.image"
        :alt="page.title"
        class="background-image"
      />
      <div class="layer-container">
        <div class="layer">
          <div class="top-content">concours</div>
          <h1>
            <template v-if="page.titleImage">
              <img
                :src="page.titleImage"
                :alt="page.title"
                width="490"
              />
              <span class="h-vhd">{{ page.title }}</span>
            </template>
            <template v-else>
              {{ capitalize(page.title) }}
            </template>
          </h1>
          <hr />
          <p class="reward">{{ page.prize }}</p>
          <div
            v-if="page.status == 'waiting' && isMobile"
            class="countdown countdown-alt"
          >
            {{ remainingDays }} {{ pluralize('jour', remainingDays) }}
            {{ pluralize('restant', remainingDays) }} <br />pour participer
          </div>
          <div
            v-if="page.withForm"
            class="with-form"
          >
            <div
              v-if="page.status === 'waiting'"
              class="button-action white"
              @click="openContest"
            >
              Participer au concours
            </div>
            <div
              v-if="page.status === 'participated'"
              class="button-action grey"
            >
              Déjà participé
            </div>
            <div
              v-if="page.status === 'ended'"
              class="button-action grey"
            >
              Concours terminé
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="page.status === 'waiting' && !isMobile && !isLargeMobile"
        class="countdown"
      >
        {{ remainingDays }} {{ pluralize('jour', remainingDays) }}
        {{ pluralize('restant', remainingDays) }} <br />pour participer
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { differenceInDays } from 'date-fns'
import { contestModal } from '~/modals/contest'

const { gtag } = useGtag()

const props = defineProps<{
  page: {
    title?: string
    titleImage?: string
    image?: string
    prize?: string
    status: string
    date?: Date
    withForm: boolean
    withAnswer: boolean
  }
}>()

const { isMobile, isLargeMobile } = useCustomMediaQuery()

const remainingDays = computed(() => {
  return props.page.date
    ? differenceInDays(new Date(props.page.date), new Date())
    : 0
})

const openContest = () => {
  gtag('event', 'cta', {
    cta_name: 'Participer au concours',
  })
  contestModal.open()
}
</script>

<style lang="scss" scoped>
.header-contest {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 90vh;
  min-height: 50rem;
  max-height: 73rem;

  .background-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    object-fit: cover;
    object-position: top;

    @media screen and (max-width: 700px) {
      position: relative;
      order: 9;
    }
  }

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    max-width: 56.6rem;
    height: 100%;
    padding: 0 8rem;
    color: $white;
    background: $blue;

    .top-content {
      margin-bottom: 1.7rem;
      color: rgba($white, 0.5);
      font-size: 1rem;
      font-weight: 500;
      text-transform: uppercase;
    }

    h1 {
      margin-bottom: 3.8rem;
      color: $white;
      font-family: SST;
      font-size: 3.2rem;
      font-weight: 400;
      letter-spacing: 0;

      img {
        display: block;
        width: 100%;

        @media (min-width: 768px) {
          width: auto;
          max-width: 100%;
        }
      }
    }

    hr {
      width: 100%;
      height: 1px;
      background-color: rgba($white, 0.2);
      border: 0;
    }

    .reward {
      margin: 3.8rem 0 3.3rem;
      font-family: 'SST Condensed';
      font-size: 2.2rem;
      font-weight: bold;
      line-height: 2.4rem;
      white-space: pre-wrap;
    }

    .button-action:not(.grey) {
      cursor: pointer;
    }
  }

  .countdown {
    position: absolute;
    top: 60%;
    left: 56.6rem;
    padding: 2.2rem 4.5rem;
    color: $blue;
    font-family: 'SST Condensed';
    font-size: 2.2rem;
    font-weight: bold;
    line-height: 2.4rem;
    text-transform: uppercase;
    background: $white;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    width: 0;
    height: 0;
    border-width: 0 0 5rem 100vw;
    border-style: solid;
    border-color: transparent transparent $white transparent;
  }
}

@media screen and (max-width: 700px) {
  .header-contest {
    align-items: flex-start;
    height: auto;
    min-height: 45rem;
    max-height: none;

    .layer-container {
      width: 100%;
    }

    .layer {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      min-height: 45rem;
      padding: 5rem 2.5rem;

      .top-content {
        width: 100%;
      }
    }

    .countdown {
      position: absolute;
      top: auto;
      bottom: 6rem;
      left: 2.5rem;

      &-alt {
        position: relative;
        top: 0;
        left: 0;
        margin-bottom: 3.3rem;
      }
    }

    .button-point-item {
      position: relative;
      bottom: auto;
      left: auto;
      margin-top: 5rem;
    }
  }
}

@media screen and (min-width: 2000px) {
  .header-contest {
    .layer-container {
      position: absolute;
      top: 0;
      left: 0;
      width: calc(50vw - 100rem + 56.6rem);
      height: 100%;
      background: $blue;

      .layer {
        right: 0;
        left: auto;
        background: transparent;
      }
    }

    .countdown {
      left: calc(50vw - 100rem + 56.6rem);
    }
  }
}
</style>
